<template>
  <ontos-form-dialog v-bind="heading" max-width="800px"
                     :saving="saving" :save-disabled="edit.unchanged"
                     :full-page-route="fullPageRoute" :dirty="edit.dirty"
                     @save="save()" @closed="$emit('closed')">
    <v-form>
      <!-- alias fixo só na edição: ele é a chave que os atributos guardam -->
      <ontos-domain-input ref="input" :value="draft" @input="draft = $event" :domains="workspace.domains"
                          :fixed-alias="!isCreating" :start-with-auto-alias="isCreating"/>
    </v-form>
  </ontos-form-dialog>
</template>
<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import DomainInput from '@/components/design/domain/DomainInput.vue'
import Domain, {emptyDomain} from '@/types/Domain'
import {EnhancedWorkpace} from '@/types/Workspace'
import {deepCopy} from '@/utils/copy'
import {DraftState, draftState} from '@/utils/modelUtils'
import {domainCreate, domainDesign, Heading} from '@/utils/heading'
import {aliasSuggestion} from '@/utils/utils'

/**
 * Edita o domínio sem sair da tela de design do ambiente. Mesma casca do fluxo de
 * trabalho, sobre o mesmo DomainInput que as páginas usam.
 *
 * Quem persiste é o host, que é um WorkspaceRouteComponent e tem a atualização reativa
 * do modelo em memória.
 */
@Component({
  emits: ['closed', 'create', 'save'],
  name: 'ontos-domain-dialog',
  components: {'ontos-domain-input': DomainInput}
})
class DomainDialog extends Vue {
  /** undefined = criando; objeto = editando aquele domínio */
  @Prop({default: undefined}) readonly domain!: Domain | undefined
  @Prop({required: true}) readonly workspace!: EnhancedWorkpace
  @Prop({type: Boolean, default: false}) readonly saving!: boolean
  /** O nome já digitado em outro lugar — o "Criar: X" do campo de domínio do atributo. */
  @Prop({type: String, default: ''}) readonly initialName!: string
  /** Desligado quando sair para a página perderia o formulário que abriu o modal. */
  @Prop({type: Boolean, default: true}) readonly fullPage!: boolean

  draft: Domain = emptyDomain()
  //Ponto de partida do rascunho: ao criar não há original, e a semente é o que o
  //usuário encontrou na tela sem ter mexido em nada.
  seed: Domain = emptyDomain()

  /**
   * No created, e não no inicializador do campo: ali as props ainda não existem, e
   * EDITAR um domínio começaria da semente de criação.
   */
  created() {
    if (this.domain) this.draft = deepCopy(this.domain)
    //A semente é o que se viu ao abrir: com o nome já vindo preenchido, fechar sem mexer
    //não pede confirmação. O alias vai junto porque o campo só o calcula quando o nome
    //MUDA, e aqui ele já chega pronto.
    else if (this.initialName) {
      this.draft = {...this.draft, name: this.initialName, alias: aliasSuggestion(this.initialName)}
    }
    this.seed = deepCopy(this.draft)
  }

  get isCreating(): boolean {
    return !this.domain
  }

  get heading(): Heading {
    return this.isCreating ? domainCreate(this.workspace) : domainDesign(this.domain!)
  }

  /** O que mudou, se o salvar habilita e se sair pergunta — a regra mora no `draftState` */
  get edit(): DraftState {
    return draftState(this.domain, this.draft, this.seed)
  }

  get fullPageRoute() {
    if (!this.fullPage) return null
    if (this.isCreating) return {name: 'create-domain', params: {...this.$route.params}}
    //a ficha, e não a edição: é onde mora o excluir, como no modal do fluxo
    return {name: 'view-domain', params: {...this.$route.params, domain: this.domain!.alias}}
  }

  save() {
    const input = this.$refs.input as any
    if (!input || !input.validateModel()) return
    if (this.isCreating) this.$emit('create', this.draft)
    else this.$emit('save', this.edit.changes)
  }
}

export default toNative(DomainDialog)
</script>
